import {
    BaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart';
import {
    STBarDir,
    STBarGrouping,
    STDLblPos,
    SubChartType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import { MarkerType } from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import { LineJoin } from '@tencent/alloy-renderer';
import isNil from 'lodash/isNil';
import { DataLabelProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label';
import type { STGrouping } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import type {
    IDataLabel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import type {
    IChartShapeProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { IBarChart } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import type {
    IBarChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/bar-chart.interface';
import type {
    ILegendItem,
    ILegendMarker,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import { IDataItemProperty } from './data-item/data-item.interface';

export class BarChartProperty extends BaseSubChartProperty<IBarChart> implements IBarChartProperty {
    public direction: STBarDir;
    public grouping: STBarGrouping;
    public gapWidth: number;

    public get overlap(): number | undefined {
        // 3D条形图与柱状图 没有overlap属性
        if (this.getChartOriginType() === SubChartType.Bar3DChart) return;
        // 二维条形图系列重叠为0时，没有overlap属性，初始化兜底数值0；
        return this.uiData.overlap ?? 0;
    }

    public init() {
        super.init();

        this.direction = this.uiData.direction ?? STBarDir.col;
        this.grouping = this.uiData.grouping ?? STBarGrouping.clustered;
        this.gapWidth = this.uiData.gapWidth ?? 100;

        return this;
    }

    public getLegendMarker(props: IChartShapeProps): ILegendMarker {
        const { border } = props.style;
        const hasStroke = border?.color || border?.gradFill;
        return {
            symbol: MarkerType.Rect,
            props: {
                style: {
                    ...props.style,
                    border: {
                        ...border,
                        ...(hasStroke ? { linejoin: border.linejoin ?? LineJoin.Round } : {}),
                    },
                },
            },
        };
    }

    public processLegendItems(items: ILegendItem[]): ILegendItem[] {
        const legendItems = super.processLegendItems(items);
        const { grouping } = this;

        // 条形图的 一个类别中的图形顺序 以及 图例顺序都是反的
        if (this.direction === STBarDir.bar && grouping === STBarGrouping.clustered) {
            return legendItems.reverse();
        }

        return legendItems;
    }

    // 图表是否是堆叠，用于坐标轴数据处理
    public isStackedGrouping(grouping: STBarGrouping) {
        return grouping === STBarGrouping.stacked;
    }

    public isPercentStackedGrouping(grouping: STBarGrouping): boolean {
        return grouping === STBarGrouping.percentStacked;
    }

    public getDataLabelProperty(dataLabel: IDataLabel, dataItem: IDataItemProperty) {
        return new BarDataLabelProperty(dataLabel, this.context).init(dataItem);
    }

    public updateSeriesOverlap(value: number, onlyChangeUI?: boolean) {
        const subChartModel = this.context.getSubChartModel() as IBarChart;
        if (subChartModel.originType !== SubChartType.BarChart) return;
        if (onlyChangeUI) {
            subChartModel.overlap = value;
            return;
        }
        subChartModel.updateProps({ overlap: value });
    }

    public getSeriesOverlap(): number | undefined  {
        const subChartModel = this.context.getSubChartModel() as IBarChart;
        if (subChartModel.originType !== SubChartType.BarChart) return;
        if (!subChartModel.overlap) {
            subChartModel.overlap = this.overlap;
        }
        return subChartModel.overlap;
    }

    public updateGapWidth(value: number, onlyChangeUI?: boolean) {
        const subChartModel = this.context.getSubChartModel() as IBarChart;
        if (subChartModel.originType  !== SubChartType.BarChart
            && subChartModel.originType !== SubChartType.Bar3DChart) return;
        if (onlyChangeUI) {
            subChartModel.gapWidth = value;
            return;
        }
        subChartModel.updateProps({ gapWidth: value });
    }

    public getGapWidth(): number | undefined {
        const subChartModel = this.context.getSubChartModel() as IBarChart;
        if (subChartModel.originType  !== SubChartType.BarChart
            && subChartModel.originType !== SubChartType.Bar3DChart) return;
        return subChartModel.gapWidth;
    }


    // protected shouldApplyThemeStyle(uiData: any) {
    //     return !uiData?.props.style.fillBackground;
    // }

    protected processData() {
        const { max, min } = this.xAxis?.scaling || {};
        if (!isNil(max)) {
            this.data = this.data.filter(i => i.category <= max);
        }
        if (!isNil(min)) {
            this.data = this.data.filter(i => i.category >= min);
        }

        super.processData();
    }
}

class BarDataLabelProperty extends DataLabelProperty {
    public get labelPosition(): STDLblPos {
        const subChart = this.context.getSubChartModel().getSubChartProperty() as IBarChartProperty;
        if (!this.uiData.labelPosition) {
            // 堆积和百分比堆积的默认位置是居中
            if (subChart.grouping === STBarGrouping.stacked || subChart.grouping === STBarGrouping.percentStacked) {
                return STDLblPos.ctr;
            }

            return STDLblPos.outEnd;
        }

        return this.uiData.labelPosition;
    }
}
